import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { TurAdimi } from './icerik';

/**
 * EKRAN TURU — spot ışığı.
 *
 * Her adım ekrandaki bir `data-tur="…"` öğesini aydınlatır: öğenin üstüne
 * dev gölgeli bir kutu konur (geri kalan karartılır), altına kart gelir.
 * Öğe yoksa (rol görmüyor, liste boş, başka sekmede) kart ortada gösterilir —
 * tur patlamaz, "bu adımda şu olurdu" der.
 *
 * Kütüphane yok; ekran boyutu/kaydırma değişince konum yeniden hesaplanır.
 */
type Kutu = { top: number; left: number; width: number; height: number } | null;

export default function Tur({ adimlar, bitir }: { adimlar: TurAdimi[]; bitir: () => void }) {
  const [i, setI] = useState(0);
  const [kutu, setKutu] = useState<Kutu>(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const adim = adimlar[i];
  const son = i === adimlar.length - 1;

  const hedefiBul = useCallback((): HTMLElement | null => {
    if (!adim?.hedef) return null;
    return document.querySelector<HTMLElement>(`[data-tur="${adim.hedef}"]`);
  }, [adim]);

  const yerlestir = useCallback(() => {
    const el = hedefiBul();
    if (!el) { setKutu(null); return; }
    const r = el.getBoundingClientRect();
    const pay = 6;
    setKutu({ top: r.top - pay, left: r.left - pay, width: r.width + pay * 2, height: r.height + pay * 2 });
  }, [hedefiBul]);

  /**
   * Adım başka ekrandaysa oraya git (ekranlar arası tur). Sayfa yüklenip
   * düğme belirene kadar kısa aralıklarla bekle (en çok ~3 sn) — veri
   * gelmeden ölçersek kutu boşa düşer.
   */
  useEffect(() => {
    if (adim?.rota && pathname !== adim.rota) navigate(adim.rota);
  }, [i, adim?.rota]); // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    let iptal = false;
    let deneme = 0;
    const dene = () => {
      if (iptal) return;
      const el = hedefiBul();
      if (el || !adim?.hedef || deneme >= 12) {
        if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        setTimeout(yerlestir, el ? 320 : 0);
        return;
      }
      deneme += 1;
      setTimeout(dene, 250);
    };
    dene();
    return () => { iptal = true; };
  }, [i, pathname, hedefiBul, yerlestir, adim?.hedef]);

  useEffect(() => {
    const yeniden = () => requestAnimationFrame(yerlestir);
    window.addEventListener('resize', yeniden);
    window.addEventListener('scroll', yeniden, true);
    return () => {
      window.removeEventListener('resize', yeniden);
      window.removeEventListener('scroll', yeniden, true);
    };
  }, [yerlestir]);

  useEffect(() => {
    const tus = (e: KeyboardEvent) => {
      if (e.key === 'Escape') bitir();
      if (e.key === 'ArrowRight') son ? bitir() : setI((x) => x + 1);
      if (e.key === 'ArrowLeft' && i > 0) setI((x) => x - 1);
    };
    document.addEventListener('keydown', tus);
    return () => document.removeEventListener('keydown', tus);
  }, [i, son, bitir]);

  if (!adim) return null;

  /** Kartı hedefin altına; altta yer yoksa üstüne; hedef yoksa ortaya. */
  const kartStil: React.CSSProperties = (() => {
    if (!kutu) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    const altBosluk = window.innerHeight - (kutu.top + kutu.height);
    const left = Math.min(Math.max(16, kutu.left), window.innerWidth - 16 - Math.min(380, window.innerWidth - 32));
    return altBosluk > 220
      ? { top: kutu.top + kutu.height + 12, left }
      : { top: Math.max(16, kutu.top - 12 - 200), left };
  })();

  return (
    <>
      <div className="ccd-tur-arka" onClick={bitir} role="presentation" />
      {kutu
        ? <div className="ccd-tur-spot" style={kutu} aria-hidden="true" />
        : <div className="ccd-tur-spot" style={{ top: 0, left: 0, width: 0, height: 0 }} aria-hidden="true" />}

      <div className="ccd-tur-kart" role="dialog" aria-live="polite" style={kartStil}>
        <div className="ccd-tur-ilerleme">Adım {i + 1} / {adimlar.length}</div>
        <h3>{adim.baslik}</h3>
        <div className="ccd-meta" style={{ fontSize: 13.5, lineHeight: 1.5 }}>{adim.metin}</div>
        {!kutu && adim.hedef && (
          <div className="ccd-meta" style={{ marginTop: 6, fontStyle: 'italic' }}>
            (Bu düğme şu an bu sayfada değil: bir kaydın içinde, başka bir sekmede ya da rolünüzün dışında.)
          </div>
        )}
        <div className="ccd-row-between" style={{ marginTop: 12 }}>
          <button className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={bitir}>Kapat</button>
          <div className="ccd-row">
            {i > 0 && (
              <button className="ccd-btn ccd-btn-sm" onClick={() => setI((x) => x - 1)}>← Önceki</button>
            )}
            <button className="ccd-btn ccd-btn-primary ccd-btn-sm"
                    onClick={() => (son ? bitir() : setI((x) => x + 1))}>
              {son ? 'Bitir ✓' : 'Sonraki →'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
